import { encodeQR } from 'qr';
import { decodeQR } from 'qr/decode.js';
import type { Image } from 'qr';

/**
 * Team token structure for QR code data
 */
export interface TeamToken {
  n: string;      // name
  w: string[];    // warrior IDs (3 items)
  r: number;      // rating
  v: 1;           // version for future compatibility
}

/**
 * Service for QR code generation and scanning
 */
export const QRService = {
  /**
   * Encode team data to a base64 string
   */
  encodeToken(name: string, warriorIds: string[], rating: number): string {
    const token: TeamToken = {
      n: name.slice(0, 20), // Limit name length
      w: warriorIds.slice(0, 3),
      r: rating,
      v: 1,
    };
    const json = JSON.stringify(token);
    return btoa(json);
  },

  /**
   * Decode base64 string to team data
   */
  decodeToken(encoded: string): TeamToken | null {
    try {
      const json = atob(encoded);
      const token = JSON.parse(json) as TeamToken;

      // Validate structure
      if (
        typeof token.n !== 'string' ||
        !Array.isArray(token.w) ||
        token.w.length !== 3 ||
        typeof token.r !== 'number' ||
        token.v !== 1
      ) {
        return null;
      }

      return token;
    } catch {
      return null;
    }
  },

  /**
   * Generate QR code as GIF data URL
   */
  generateTeamQR(name: string, warriorIds: string[], rating: number): string {
    const encoded = this.encodeToken(name, warriorIds, rating);
    const gifBytes = encodeQR(encoded, 'gif', { scale: 4 });

    // Convert Uint8Array to base64 data URL
    const base64 = btoa(String.fromCharCode(...gifBytes));
    return `data:image/gif;base64,${base64}`;
  },

  /**
   * Generate QR code as raw GIF bytes (for rendering)
   */
  generateTeamQRBytes(name: string, warriorIds: string[], rating: number): Uint8Array {
    const encoded = this.encodeToken(name, warriorIds, rating);
    return encodeQR(encoded, 'gif', { scale: 4 });
  },

  /**
   * Decode QR code from image data
   * @param image - Image object with data, width, height
   */
  decodeFromImage(image: Image): TeamToken | null {
    try {
      const decoded = decodeQR(image);
      if (!decoded) return null;

      return this.decodeToken(decoded);
    } catch {
      return null;
    }
  },

  /**
   * Validate that warrior IDs exist in available warriors
   */
  validateWarriorIds(warriorIds: string[], availableIds: string[]): boolean {
    return warriorIds.every(id => availableIds.includes(id));
  },
};
